import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import type { Citation } from "@/api/types";
import type { HighlightEntity } from "@/components/artifacts/entityHighlight";
import { HighlightedText } from "@/components/artifacts/HighlightedText";
import {
  splitTextByMarkers,
  type CitationSegment,
} from "@/components/artifacts/inlineCitations";
import { cn } from "@/lib/utils";

export interface InlineCitedTextProps {
  /**
   * Source body containing `[[cN]]` marker tokens. Adjacent text is rendered
   * verbatim; markers are replaced by inline citation chips.
   */
  text: string;
  /**
   * Citations array; the `N` in `[[cN]]` indexes into this array. Out-of-range
   * indices are silently dropped (LLM drift guard) — the surrounding text
   * still renders intact.
   */
  citations: Citation[];
  /**
   * Click handler for a chip. Receives the citation's `start_ms`. When `null`
   * (citation has no timestamp) the chip is rendered but is non-interactive.
   */
  onJump?: (startMs: number) => void;
  /**
   * Phase 6: bound employees/clients to highlight inside plain text
   * segments. Citation chips themselves are not subject to highlighting.
   */
  highlightEntities?: readonly HighlightEntity[];
}

const MAX_TOOLTIP_QUOTE = 240;

function formatTimestamp(ms: number): string {
  const safe = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}

/**
 * Inline question-mark icon for `verified=false` chips. Mirrors the SVG used in
 * CitationBlock so the visual language stays consistent across the inline and
 * fallback renderers.
 */
function HelpCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="10"
      height="10"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

interface CitationChipProps {
  citation: Citation;
  citationIndex: number;
  onJump?: (startMs: number) => void;
}

function CitationChip({ citation, citationIndex, onJump }: CitationChipProps) {
  const { t } = useTranslation();
  const hasTimestamp =
    typeof citation.start_ms === "number" && citation.start_ms >= 0;
  const isClickable = hasTimestamp && onJump !== undefined;
  const noTimecodeLabel = t("meetings.card.citations.noTimecodeLabel");
  const timestamp = hasTimestamp
    ? formatTimestamp(citation.start_ms as number)
    : noTimecodeLabel;
  const verifiedOff = citation.verified === false;

  // Truncate the tooltip text — native `title` tooltips in some browsers cap
  // their width before wrapping, and we want the operator to glance not read.
  const fullQuote = citation.quote ?? "";
  const tooltipQuote =
    fullQuote.length > MAX_TOOLTIP_QUOTE
      ? `${fullQuote.slice(0, MAX_TOOLTIP_QUOTE).trimEnd()}…`
      : fullQuote;
  const tooltip = hasTimestamp
    ? t("meetings.card.citations.inlineTooltip", {
        quote: tooltipQuote,
        timestamp,
      })
    : t("meetings.card.citations.inlineTooltipNoTime", { quote: tooltipQuote });
  const ariaLabel = hasTimestamp
    ? t("meetings.card.citations.jumpAria", { time: timestamp })
    : t("meetings.card.citations.noTimecodeAria");

  const chipBase = cn(
    "mx-0.5 inline-flex items-center gap-0.5 rounded px-1 py-px align-baseline",
    "font-mono text-[10px] tabular-nums",
    isClickable
      ? "bg-blue-50 text-blue-700 hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      : "bg-muted text-muted-foreground",
  );

  const handleClick = () => {
    if (isClickable) {
      onJump(citation.start_ms as number);
    }
  };

  const label = `[${timestamp}]`;

  const inner = (
    <>
      <span>{label}</span>
      {verifiedOff && (
        <HelpCircleIcon
          className="inline-block"
          // Test hook lives on the parent chip; the icon is presentational.
        />
      )}
    </>
  );

  // CSS-only hover/focus popover. The native `title` attribute fires only
  // after a ~1-2s browser delay, which operators reported as "tooltips
  // don't appear". Showing a styled bubble on :hover / :focus-within
  // gives instant feedback while keeping aria-label as the AT-only path.
  const bubble = (
    <span
      role="tooltip"
      className={cn(
        "pointer-events-none absolute bottom-full left-1/2 z-50 hidden",
        "w-max max-w-xs -translate-x-1/2 -translate-y-1",
        "whitespace-pre-line rounded-md border bg-popover px-2 py-1",
        "text-xs leading-snug text-popover-foreground shadow-md",
        "group-hover:block group-focus-within:block",
      )}
      data-testid="inline-citation-tooltip"
    >
      {tooltip}
    </span>
  );

  const wrapperClass = "group relative inline-block";

  if (isClickable) {
    return (
      <span className={wrapperClass}>
        <button
          type="button"
          onClick={handleClick}
          className={chipBase}
          aria-label={ariaLabel}
          data-testid="inline-citation-chip"
          data-citation-index={citationIndex}
          data-verified={verifiedOff ? "false" : "true"}
        >
          {inner}
        </button>
        {bubble}
      </span>
    );
  }

  return (
    <span className={wrapperClass}>
      <span
        className={chipBase}
        aria-label={ariaLabel}
        tabIndex={0}
        data-testid="inline-citation-chip"
        data-citation-index={citationIndex}
        data-verified={verifiedOff ? "false" : "true"}
      >
        {inner}
      </span>
      {bubble}
    </span>
  );
}

/**
 * Render a text body with inline citation chips at every `[[cN]]` marker.
 *
 * - text-only segments → rendered verbatim (whitespace preserved; line breaks
 *   left to the caller's wrapping element — usually a <p> or <li>).
 * - marker segments → small clickable chip showing `[MM:SS]`. Out-of-range
 *   markers are skipped, surrounding text is preserved.
 * - missing `onJump` or missing `start_ms` → chip renders as a static badge
 *   (still tooltipped, but no click).
 */
export function InlineCitedText({
  text,
  citations,
  onJump,
  highlightEntities,
}: InlineCitedTextProps) {
  const segments = splitTextByMarkers(text);

  if (segments.length === 0) {
    return null;
  }

  return (
    <span data-testid="inline-cited-text">
      {segments.map((segment, i) =>
        renderSegment(segment, i, citations, onJump, highlightEntities),
      )}
    </span>
  );
}

function renderSegment(
  segment: CitationSegment,
  key: number,
  citations: Citation[],
  onJump?: (startMs: number) => void,
  highlightEntities?: readonly HighlightEntity[],
) {
  if (segment.type === "text") {
    if (highlightEntities !== undefined && highlightEntities.length > 0) {
      return (
        <Fragment key={key}>
          <HighlightedText
            text={segment.content}
            entities={highlightEntities}
          />
        </Fragment>
      );
    }
    return <Fragment key={key}>{segment.content}</Fragment>;
  }
  // Marker: look up the citation. Drop out-of-range markers entirely (the LLM
  // sometimes drifts and emits indices past the end of the array).
  const citation = citations[segment.citationIndex];
  if (citation === undefined) {
    return null;
  }
  return (
    <CitationChip
      key={key}
      citation={citation}
      citationIndex={segment.citationIndex}
      onJump={onJump}
    />
  );
}
